"use client";

import { useEffect, useRef, useCallback, useMemo } from "react";
import Image from "next/image";

const MetaPixel = ({ pixelId, abandonedCartTimeout = 30 }) => {
  const cartTimerRef = useRef(null);
  const cartItemsRef = useRef([]);

  // Cart management logic
  const cartManagement = useMemo(
    () => ({
      addToCart: (item) => {
        cartItemsRef.current.push({
          ...item,
          addedAt: new Date().toISOString(),
        });

        // Reset abandonment timer
        if (cartTimerRef.current) {
          clearTimeout(cartTimerRef.current);
        }

        // Set new abandonment timer
        cartTimerRef.current = setTimeout(() => {
          if (cartItemsRef.current.length > 0) {
            const totalValue = cartItemsRef.current.reduce(
              (sum, item) => sum + (parseFloat(item.value) || 0),
              0
            );

            window.trackCustomEvent("AbandonedCart", {
              cartContents: cartItemsRef.current,
              totalValue: totalValue.toFixed(2),
              currency: cartItemsRef.current[0]?.currency || "INR",
              abandonedAt: new Date().toISOString(),
              timeToAbandon: abandonedCartTimeout,
            });
          }
        }, abandonedCartTimeout * 60 * 1000);
      },
      clearCart: () => {
        cartItemsRef.current = [];
        if (cartTimerRef.current) {
          clearTimeout(cartTimerRef.current);
        }
      },
    }),
    [abandonedCartTimeout]
  );

  // Tracking product view
  const trackProductView = useCallback((product) => {
    if (typeof window === "undefined" || !window.fbq) return;

    try {
      window.fbq("track", "ViewContent", {
        content_ids: [product.id],
        content_name: product.productName,
        value: product.productPrice,
        currency: product.currency || "INR",
        contents: [
          {
            id: product.id,
            item_price: product.productPrice,
            quantity: product.quantity || 1,
            content_name: product.productName,
          },
        ],
        content_category: product.productCategory || "",
        availability: "in stock",
      });
    } catch (error) {
      console.error("Meta Pixel View Content Tracking Error:", error);
    }
  }, []);

  // Tracking add to cart
  const trackAddToCart = useCallback(
    (product) => {
      if (typeof window === "undefined" || !window.fbq) return;

      try {
        const cartItem = {
          content_ids: [product.id],
          content_name: product.productName,
          value: product.productPrice * (product.quantity || 1),
          currency: product.currency || "INR",
          contents: [
            {
              id: product.id,
              quantity: product.quantity || 1,
              item_price: product.productPrice,
              content_name: product.productName,
            },
          ],
          content_category: product.productCategory || "",
          availability: "in stock",
        };

        cartManagement.addToCart(cartItem);

        window.fbq("track", "AddToCart", cartItem);
      } catch (error) {
        console.error("Meta Pixel Add to Cart Tracking Error:", error);
      }
    },
    [cartManagement]
  );

  // Tracking purchase
  const trackPurchase = useCallback(
    (orderDetails) => {
      if (typeof window === "undefined" || !window.fbq) return;

      try {
        const purchaseEvent = {
          value: orderDetails.totalValue,
          currency: "INR",
          contents: orderDetails.products.map((product) => ({
            id: product.id,
            quantity: product.quantity || 1,
            item_price: product.productPrice,
            content_name: product.productName,
            content_category: product.productCategory || "",
          })),
          num_items: orderDetails.products.length,
          transaction_id: orderDetails.orderId,
          shipping: orderDetails.shipping || 0,
          tax: orderDetails.tax || 0,
        };

        cartManagement.clearCart();

        window.fbq("track", "Purchase", purchaseEvent);
      } catch (error) {
        console.error("Meta Pixel Purchase Tracking Error:", error);
      }
    },
    [cartManagement]
  );

  // Initialize Meta Pixel and expose global methods
  useEffect(() => {
    if (typeof window === "undefined" || !pixelId) return;

    // Load Meta Pixel script
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

    window.fbq("init", pixelId);
    window.fbq("track", "PageView");

    // Expose tracking methods globally
    window.metaPixelTracking = {
      trackProductView,
      trackAddToCart,
      trackPurchase,
    };

    return () => {
      if (cartTimerRef.current) clearTimeout(cartTimerRef.current);
    };
  }, [pixelId, trackProductView, trackAddToCart, trackPurchase]);

  return (
    <noscript>
      <Image
        alt="Meta Pixel"
        height="1"
        width="1"
        style={{ display: "none" }}
        src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
      />
    </noscript>
  );
};

export default MetaPixel;
